import React from 'react';
import ImageEditor from '@toast-ui/react-image-editor';
import { connect } from 'react-redux';
import { Alert, Button, Loader, IconButton, Icon } from 'rsuite';
import PropTypes from 'prop-types';
import {
  createAssetPresignedUrl,
  getElementHasUploadError,
  uploadAssetToBucket,
  getElementPresignedUrl,
  getElementMediaUrl,
  getElementEditMode,
  getHasEditorChanged,
  getHasFinishedLoading,
  editImageElementImage,
  loadImageElementImage,
  selectImageElementImage,
  saveEditedImage,
  resetImageEditorState,
  getElementLoadedImage,
  getElementIsLoading,
  deleteElement,
  setIsEditedState,
  setFinishedLoadingState
} from '../../redux/modules/campaigns';
import CloudIcon from './svg/CloudIcon';
import 'tui-image-editor/dist/tui-image-editor.css';
import styles from './ImageElement.module.less';
import { dataURItoBlob } from '../../utils/imageHelpers';
import ElementSortButtons from './ElementSortButtons';
import PromptLabelContainer from './PromptLabelContainer';
import convertBlobToBase64 from '../../utils/blobToBase64';
import { VpButton, VpIcon } from '../../vp-ui/components';

const imageEditorTheme = {
  'menu.iconSize.width': '16px',
  'menu.iconSize.height': '16px',

  'submenu.iconSize.width': '16px',
  'submenu.iconSize.height': '16px',
  'submenu.backgroundColor': '#fff',

  'common.backgroundColor': '#fff',

  // checkbox style
  'checkbox.border': '1px solid #ccc',
  'checkbox.backgroundColor': '#fff'
};

const CONTAINER_WIDTH = 700;
const CONTAINER_HEIGHT = 500;

const toastImageEditorUiOptions = {
  theme: imageEditorTheme,
  menu: ['resize', 'crop', 'flip', 'rotate', 'filter'],
  uiSize: {
    width: '100%',
    height: `${CONTAINER_HEIGHT}px`
  },
  menuBarPosition: 'bottom'
};

const DeleteImageButton = ({ idx, deleteImageHandler }) => (
  <div className={styles.vpReUploadContainer}>
    <Button
      appearance="ghost"
      onClick={() => deleteImageHandler(idx)}
      className={styles.vpDeleteImageButton}
    >
      Delete
    </Button>
  </div>
);

const ClearImageButton = ({ idx, clearImageHandler }) => (
  <div className={styles.vpReUploadContainer}>
    <Button
      appearance="ghost"
      onClick={() => clearImageHandler(idx)}
      className={styles.vpDeleteImageButton}
    >
      Clear
    </Button>
  </div>
);

export class ImageElement extends React.Component {
  async componentDidMount() {
    const { mediaUrl, idx } = this.props;

    if (mediaUrl) {
      this.props._loadImageElementImage(idx, mediaUrl);
    }
  }

  async componentDidUpdate() {
    const {
      presignedUrl,
      idx,
      mediaUrl,
      loadedImage,
      uploadSuccessMessage,
      isLoading,
      isCustomSuccessMessage = false,
      finishedLoading = false,
      _setFinishedLoadingState
    } = this.props;
    const editorInstance = this.editorRef.current.getInstance();

    // if there's a presigned url on the redux store it means the user has just requested to
    // upload a new image, here is where we actually call to upload it to S3
    if (presignedUrl) {
      try {
        const dataUri = editorInstance.toDataURL();

        const imageToUpload = dataURItoBlob(dataUri);

        await this.props._uploadAssetToBucket(imageToUpload, presignedUrl, idx);

        if (!isCustomSuccessMessage) {
          const successMessage =
            uploadSuccessMessage ||
            'Your image has been uploaded, now click the "Save and Publish" button if you wish to permanently save this change to your Campaign.';
          Alert.info(successMessage, 8000);
        }
        return;
      } catch (error) {
        console.log('Error getting image to upload: ', error);
      }
    }

    if (mediaUrl && !loadedImage && !isLoading && !finishedLoading) {
      try {
        // random number in url necessary to avoid cors error:
        // https://stackoverflow.com/questions/1077041/refresh-image-with-a-new-one-at-the-same-url/22429796#22429796
        await editorInstance.loadImageFromURL(
          `${mediaUrl}?t=${Math.random()}`,
          'imageElementImage'
        );

        // need to explicitly call this otherwise the menu doesnt work after loading an image
        // it's a random issue with the lib, more info on https://github.com/nhn/tui.image-editor/issues/103
        await editorInstance.ui.activeMenuEvent();
        if (!finishedLoading) {
          _setFinishedLoadingState(idx);
        }
      } catch (error) {
        console.log('Error loading existing media: ', error);
      }
    }

    if (loadedImage && !isLoading) {
      try {
        const currentImage = editorInstance.toDataURL();
        const loadedImgDataUri = await convertBlobToBase64(loadedImage);
        if (loadedImgDataUri === currentImage) {
          return;
        }

        await editorInstance.loadImageFromFile(loadedImage);
        await editorInstance.ui.activeMenuEvent();
      } catch (error) {
        console.log('Error loading image: ', error);
      }
    }
  }

  editorRef = React.createRef();
  fileInputRef = React.createRef();

  fileSelectedHandler = (event) => {
    const { idx } = this.props;
    this.props._selectImageElementImage(idx, event.target.files[0]);
  };

  handleSaveEditedImage = async () => {
    const { idx } = this.props;

    // saving the image generates a presigned url and persists it to the redux store
    // after this, componentWillUpdate will run and handle uploading the file to that url
    this.props._getPresignedUrl(idx);
  };

  handleCancel = async () => {
    const { idx } = this.props;
    this.props._editImageElementImage(idx, false);
  };

  handleUndoStackChanged = () => {
    const { idx, _setIsEditedState = () => {}, hasEditorChanged, finishedLoading } = this.props;
    if (!hasEditorChanged && finishedLoading) {
      _setIsEditedState(idx, true);
    }
  };

  render() {
    const {
      editMode,
      mediaUrl,
      isLoading,
      hasUploadError,
      idx,
      _deleteElement,
      _resetImageEditorState,
      element,
      showClearButton = false,
      hideDeleteButton = false,
      hideDeleteButtonMissingImage = false,
      hideActionButtons = false,
      disableMargins = false,
      disabled = false,
      disableSort = false,
      isMediaSubmissionsPage = false,
      hasEditorChanged = false
    } = this.props;

    const changeImageButtonDisabled =
      this.props._setIsEditedState === undefined ? isLoading : !hasEditorChanged || isLoading;

    const emptyIconState = !editMode && !mediaUrl;
    const displayImageState = !editMode && mediaUrl;

    // image editor is hidden using css so that it's ref is always available to interact with
    const displayEditorState = editMode;

    const containerClasses = `
      ${disableMargins ? styles.vpCustomImageEditorNoMargins : ''}
      ${styles.vpCustomImageEditor}
      ${!disableSort ? styles.vpCustomImageEditorSortable : ''}
    `;

    const selectNewFileButtonLabel = isMediaSubmissionsPage ? 'Change Thumbnail' : 'Change Image';
    const saveChangesButtonLabel = isMediaSubmissionsPage ? 'Save Changes' : 'Save';
    const buttonsContainerClasses = `${styles.vpReUploadContainer} ${
      isMediaSubmissionsPage && styles.mediaSubmissionsButtonOrientation
    }`;

    return (
      <section className={containerClasses}>
        {!disableSort && <ElementSortButtons idx={idx} />}
        <div style={{ display: displayEditorState ? 'block' : 'none' }}>
          <ImageEditor
            ref={this.editorRef}
            includeUI={toastImageEditorUiOptions}
            cssMaxHeight={CONTAINER_HEIGHT}
            cssMaxWidth={CONTAINER_WIDTH}
            usageStatistics={false}
            onUndoStackChanged={this.handleUndoStackChanged}
          />
          {!hideActionButtons && (
            <div className={buttonsContainerClasses}>
              <div className={styles.vpActionsLeftContainer}>
                <VpButton
                  variant="outlined"
                  onClick={this.handleCancel}
                  className={styles.vpImageEditingButton}
                  disabled={isLoading}
                >
                  Cancel
                </VpButton>
              </div>
              <div className={styles.vpActionsRightContainer}>
                <label
                  style={{ cursor: isLoading ? 'auto' : 'pointer' }}
                  htmlFor={`imageReUploadButton-${idx}`}
                  className={styles.vpReUploadImageButton}
                >
                  <VpButton
                    loadingIconSize={25}
                    loading={isLoading}
                    disabled={isLoading}
                    variant="outlined"
                    sx={{
                      fontSize: '14px',
                      pointerEvents: 'none'
                    }}
                    className={styles.vpImageEditingButton}
                    onClick={() => {}}
                    startIcon={<VpIcon icon="Image" />}
                  >
                    {selectNewFileButtonLabel}
                  </VpButton>
                </label>
                <input
                  disabled={isLoading}
                  accept="image/*"
                  id={`imageReUploadButton-${idx}`}
                  className={styles.vpHiddenInput}
                  type="file"
                  onChange={this.fileSelectedHandler}
                />
                <VpButton
                  loadingIconSize={25}
                  loading={isLoading}
                  disabled={changeImageButtonDisabled}
                  variant="contained"
                  sx={{ fontSize: '14px' }}
                  className={styles.vpSaveButton}
                  onClick={this.handleSaveEditedImage}
                  startIcon={<VpIcon icon="Save" />}
                >
                  {saveChangesButtonLabel}
                </VpButton>
              </div>
            </div>
          )}
        </div>
        {!displayEditorState && (
          <>
            {emptyIconState && !isLoading && !hasUploadError && (
              <div className={styles.vpUploadInstructionsContainer}>
                <label
                  htmlFor={`imageUploadInput-${idx}`}
                  className={`${styles.vpImageUploadLabel} ${
                    disabled ? styles.vpImageUploadLabelDisabled : ''
                  } ${disableSort ? '' : styles.vpImageUploadLabelNarrow}`}
                >
                  <div className={styles.vpAddImageIconContainer}>
                    <CloudIcon />
                    <div>Add an image</div>
                    <div>(Optional)</div>
                  </div>
                  <div className={styles.vpImageSupportContainer}>
                    <div>We support JPG and PNG formats.</div>
                    <div>Typically files under 100kb will perform the best.</div>
                  </div>
                </label>
                <input
                  accept="image/*"
                  id={`imageUploadInput-${idx}`}
                  className={styles.vpHiddenInput}
                  type="file"
                  onChange={this.fileSelectedHandler}
                  disabled={disabled}
                />
                {!hideDeleteButton && !hideDeleteButtonMissingImage && disableSort && (
                  <DeleteImageButton idx={idx} deleteImageHandler={_deleteElement} />
                )}
              </div>
            )}
            {displayImageState && !isLoading && !hasUploadError && (
              <>
                <div className={styles.vpDisplayImageContainer}>
                  <div className={styles.vpImageContainer}>
                    <img
                      src={mediaUrl}
                      alt=""
                      style={{
                        maxWidth: `${CONTAINER_WIDTH}px`,
                        maxHeight: `${CONTAINER_HEIGHT}px`
                      }}
                    />
                    <IconButton
                      className={styles.vpImageEditButton}
                      appearance="primary"
                      icon={<Icon icon="edit2" />}
                      onClick={() => this.props._editImageElementImage(idx, true)}
                    />
                  </div>
                  {!hideDeleteButton && disableSort && (
                    <DeleteImageButton idx={idx} deleteImageHandler={_deleteElement} />
                  )}
                  {showClearButton && (
                    <ClearImageButton idx={idx} clearImageHandler={_resetImageEditorState} />
                  )}
                </div>
              </>
            )}
            {isLoading && (
              <Loader
                content="Please wait while your image is uploaded..."
                vertical
                className={styles.vpImageLoader}
              />
            )}
            {hasUploadError && (
              <div className={styles.vpImageLoader}>
                There was an error uploading your image, please try again.
                <Button
                  appearance="primary"
                  className={styles.vpImageEditingButton}
                  onClick={() => this.props._resetImageEditorState(idx)}
                >
                  Ok
                </Button>
              </div>
            )}
          </>
        )}
        {!disableSort && element && (
          <PromptLabelContainer
            crakId={element?.crakId}
            campaignElementId={element?.elementTypeId}
            idx={idx}
          />
        )}
      </section>
    );
  }
}

ImageElement.propTypes = {
  isLoading: PropTypes.bool,
  isSuccessful: PropTypes.bool,
  customSaveButton: PropTypes.node,
  isCustomSuccessMessage: PropTypes.bool,
  presignedUrl: PropTypes.string,
  idx: PropTypes.number,
  mediaUrl: PropTypes.string,
  loadedImage: PropTypes.file,
  uploadSuccessMessage: PropTypes.string,
  hasUploadError: PropTypes.bool,
  editMode: PropTypes.bool,
  _editImageElementImage: PropTypes.func.isRequired,
  _loadImageElementImage: PropTypes.func.isRequired,
  _uploadAssetToBucket: PropTypes.func.isRequired,
  _getPresignedUrl: PropTypes.func.isRequired,
  _selectImageElementImage: PropTypes.func.isRequired,
  _saveEditedImage: PropTypes.func.isRequired,
  _resetImageEditorState: PropTypes.func.isRequired,
  _deleteElement: PropTypes.func.isRequired,
  _setIsEditedState: PropTypes.func,
  _setFinishedLoadingState: PropTypes.func,
  finishedLoading: PropTypes.bool,
  hasEditorChanged: PropTypes.bool,
  showClearButton: PropTypes.bool,
  hideDeleteButton: PropTypes.bool,
  isMediaSubmissionsPage: PropTypes.bool,
  hideDeleteButtonMissingImage: PropTypes.bool,
  hideActionButtons: PropTypes.bool,
  disableMargins: PropTypes.bool,
  disabled: PropTypes.bool,
  disableSort: PropTypes.bool,
  element: PropTypes.shape({
    crakId: PropTypes.string,
    elementTypeId: PropTypes.string
  })
};

DeleteImageButton.propTypes = {
  idx: PropTypes.number,
  deleteImageHandler: PropTypes.func
};

ClearImageButton.propTypes = {
  idx: PropTypes.number,
  clearImageHandler: PropTypes.func
};

function mapStateToProps(state, ownProps) {
  return {
    hasUploadError: getElementHasUploadError(state, ownProps.idx),
    presignedUrl: getElementPresignedUrl(state, ownProps.idx),
    mediaUrl: getElementMediaUrl(state, ownProps.idx),
    editMode: getElementEditMode(state, ownProps.idx),
    loadedImage: getElementLoadedImage(state, ownProps.idx),
    isLoading: getElementIsLoading(state, ownProps.idx),
    hasEditorChanged: getHasEditorChanged(state, ownProps.idx),
    finishedLoading: getHasFinishedLoading(state, ownProps.idx)
  };
}

const mapDispatchToProps = {
  _getPresignedUrl: createAssetPresignedUrl,
  _uploadAssetToBucket: uploadAssetToBucket,
  _editImageElementImage: editImageElementImage,
  _loadImageElementImage: loadImageElementImage,
  _selectImageElementImage: selectImageElementImage,
  _saveEditedImage: saveEditedImage,
  _resetImageEditorState: resetImageEditorState,
  _deleteElement: deleteElement,
  _setIsEditedState: setIsEditedState,
  _setFinishedLoadingState: setFinishedLoadingState
};

export default connect(mapStateToProps, mapDispatchToProps)(ImageElement);



// WEBPACK FOOTER //
// ./src/components/CampaignEdit/ImageElement.jsx